import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Select,
  Alert,
  Snackbar,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  OutlinedInput,
  Button,
  Stack,
} from '@mui/material';
import { Portfolio } from '../../types/Portfolio';


interface InvestorContextFormProps {
  investorContext: Portfolio['investorContext'];
  onSave: (data: any) => void; // calls updatePortfolioMeta({ investorContext: data })
}

const fundingTypeOptions = [
  'corporate_sponsorship',
  'foundation_grant',
  'government_grant',
  'individual_donor',
  'partnership',
  'philanthropy',
  'csr_program',
  'brand_collaboration',
];

const industryOptions = [
  'Beverage',
  'Finance',
  'Technology',
  'Tourism',
  'Media',
  'Education',
  'Other',
];

const scaleOptions = ['local', 'national', 'international', 'global'];

const decisionMakerOptions = [
  'marketing',
  'csr',
  'foundation',
  'executive',
  'agency',
  'unknown',
];

const relationshipStageOptions = [
  'prospect',
  'initial_contact',
  'negotiation',
  'active_partner',
  'lapsed',
  'closed',
];

const priorityOptions = ['low', 'medium', 'high', 'urgent'];

const InvestorContextForm: React.FC<InvestorContextFormProps> = ({
  investorContext = {},
  onSave,
}) => {
  const [formData, setFormData] = useState<any>(investorContext || {});
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: 'success' | 'error' | 'info'}>({open: false, message: '', severity: 'info'});


  const handleChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field: string, value: string) => {
    const items = value.split('\n').filter((v) => v.trim());
    setFormData((prev: any) => ({ ...prev, [field]: items }));
  };

const handleSave = async () => {
  setSaving(true);
  try {
    await onSave(formData);
    setSnackbar({ open: true, message: 'Investor context saved successfully.', severity: 'success' });
  } catch (err) {
    console.error('Error saving investor context:', err);
    setSnackbar({ open: true, message: 'Error saving investor context.', severity: 'error' });
  } finally {
    setSaving(false);
  }
};

  return (
    <Box
      sx={{
        bgcolor: '#2C3E50',
        borderBottom: '1px solid #3A9188',
        color: '#FFFFFF',
        px: { xs: 3, md: 5 },
        py: 4,
      }}
    >
      <Typography
        variant="h5"
        sx={{ mb: 3, fontWeight: 700, color: '#3A9188' }}
      >
        Investor Context (Internal)
      </Typography>

      <Grid container spacing={3}>
        {/* Target Investor */}
        <Grid item xs={12} md={6}>
          <TextField
            label="Target Investor"
            fullWidth
            value={formData.targetInvestor || ''}
            onChange={(e) => handleChange('targetInvestor', e.target.value)}
            sx={{ bgcolor: '#34495E' }}
            InputLabelProps={{ style: { color: '#A4B0BE' } }}
            InputProps={{ style: { color: '#FFFFFF' } }}
          />
        </Grid>
                {/* Contact Name */}
        <Grid item xs={12} md={6}>
          <TextField
            label="Prepared For/Contact Name"
            fullWidth
            value={formData.contactName || ''}
            onChange={(e) => handleChange('contactName', e.target.value)}
            sx={{ bgcolor: '#34495E' }}
            InputLabelProps={{ style: { color: '#A4B0BE' } }}
            InputProps={{ style: { color: '#FFFFFF' } }}
          />
        </Grid>

        {/* Contact Email */}
        <Grid item xs={12} md={6}>
          <TextField
            label="Contact Email"
            type="email"
            fullWidth
            value={formData.contactEmail || ''}
            onChange={(e) => handleChange('contactEmail', e.target.value)}
            sx={{ bgcolor: '#34495E' }}
            InputLabelProps={{ style: { color: '#A4B0BE' } }}
            InputProps={{ style: { color: '#FFFFFF' } }}
          />
        </Grid>

        {/* Priority */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel sx={{ color: '#A4B0BE' }}>Priority</InputLabel>
            <Select
              value={formData.priority || ''}
              onChange={(e) => handleChange('priority', e.target.value)}
              sx={{
                bgcolor: '#34495E',
                color: '#FFFFFF',
                '& .MuiSelect-icon': { color: '#3A9188' },
              }}
            >
              {priorityOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Funding Type */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel sx={{ color: '#A4B0BE' }}>Funding Type</InputLabel>
            <Select
              multiple
              value={formData.fundingType || []}
              onChange={(e) => handleChange('fundingType', e.target.value)}
              input={<OutlinedInput label="Funding Type" />}
              sx={{
                bgcolor: '#34495E',
                color: '#FFFFFF',
                '& .MuiSelect-icon': { color: '#3A9188' },
              }}
              renderValue={(selected: string[]) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip
                      key={value}
                      label={value}
                      sx={{ bgcolor: '#3A9188', color: '#fff' }}
                    />
                  ))}
                </Box>
              )}
            >
              {fundingTypeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Industry */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel sx={{ color: '#A4B0BE' }}>Industry</InputLabel>
            <Select
              value={formData.industry || ''}
              onChange={(e) => handleChange('industry', e.target.value)}
              sx={{ bgcolor: '#34495E', color: '#FFFFFF' }}
            >
              {industryOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Scale */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel sx={{ color: '#A4B0BE' }}>Scale</InputLabel>
            <Select
              value={formData.scale || ''}
              onChange={(e) => handleChange('scale', e.target.value)}
              sx={{ bgcolor: '#34495E', color: '#FFFFFF' }}
            >
              {scaleOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Relationship Stage */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel sx={{ color: '#A4B0BE' }}>Relationship Stage</InputLabel>
            <Select
              value={formData.relationshipStage || ''}
              onChange={(e) =>
                handleChange('relationshipStage', e.target.value)
              }
              sx={{ bgcolor: '#34495E', color: '#FFFFFF' }}
            >
              {relationshipStageOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Objectives */}
        <Grid item xs={12}>
          <TextField
            label="Sponsor Objectives"
            fullWidth
            multiline
            minRows={3}
            value={(formData.objectives || []).join('\n')}
            onChange={(e) =>
              handleArrayChange('objectives', e.target.value)
            }
            helperText="One objective per line"
            sx={{ bgcolor: '#34495E' }}
            InputLabelProps={{ style: { color: '#A4B0BE' } }}
            InputProps={{ style: { color: '#FFFFFF' } }}
          />
        </Grid>

        {/* Similar Sponsorships */}
        <Grid item xs={12}>
          <TextField
            label="Similar Sponsorships"
            fullWidth
            multiline
            minRows={2}
            value={(formData.similarSponsorships || []).join('\n')}
            onChange={(e) =>
              handleArrayChange('similarSponsorships', e.target.value)
            }
            helperText="One example per line"
            sx={{ bgcolor: '#34495E' }}
            InputLabelProps={{ style: { color: '#A4B0BE' } }}
            InputProps={{ style: { color: '#FFFFFF' } }}
          />
        </Grid>

        {/* Notes */}
        <Grid item xs={12}>
          <TextField
            label="Internal Notes"
            fullWidth
            multiline
            minRows={4}
            value={formData.notes || ''}
            onChange={(e) => handleChange('notes', e.target.value)}
            sx={{ bgcolor: '#34495E' }}
            InputLabelProps={{ style: { color: '#A4B0BE' } }}
            InputProps={{ style: { color: '#FFFFFF' } }}
          />
        </Grid>
      </Grid>

      {/* Save Button */}
   <Stack direction="row" justifyContent="flex-end" sx={{ mt: 4 }}>
  <Button
    variant="contained"
    onClick={handleSave}
    disabled={saving}
    sx={{
      bgcolor: '#3A9188',
      color: '#fff',
      fontWeight: 600,
      px: 4,
      '&:hover': { bgcolor: '#2E736A' },
    }}
  >
    {saving ? 'Saving...' : 'Save Changes'}
  </Button>

  <Snackbar
    open={snackbar.open}
    autoHideDuration={3000}
    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
  >
    <Alert
      onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      severity={snackbar.severity}
      sx={{
        bgcolor:
          snackbar.severity === 'success'
            ? '#3A9188'
            : snackbar.severity === 'error'
            ? '#C62828'
            : '#34495E',
        color: '#fff',
        fontWeight: 600,
        borderRadius: 2,
      }}
    >
      {snackbar.message}
    </Alert>
  </Snackbar>
</Stack>
    </Box>
  );
};

export default InvestorContextForm;